// File: frontend/src/pages/TenantToday.js
// Version: 1.0 (2026-09-28, Phase 9 — план 09-05)
// Description: экран «Today» для арендатора с признаком `today_dashboard` (первым — ELN).
//              Семь блоков: консультации сегодня, мои задачи, ближайший срок, новые
//              обращения за сутки, записи, звонки и чаты Sofia, звонки без обращения.
//
// 🔴 РУБЕЖ ПРИЗНАКА СТОИТ НЕ ЗДЕСЬ, А В Dashboard.js (план 09-06): второго рубежа этот
//    файл не заводит — два держателя одного решения разошлись бы молча.
//
// 🔴 НИ ОДНОГО ЛИТЕРАЛА ФИРМЫ. Название — `branding.company_name` (иначе `firma`
//    главного офиса из /ofisy/, иначе ничего); адрес In-person — `adres_naruzhu`
//    главного офиса из /ofisy/; условия консультации — /today/bookings. Все тексты
//    интерфейса — из словаря `i18n/today.js` через `useUiLanguage().t`.
//
// 🔴 НОМЕРА ФИРМЫ НЕТ ВОВСЕ — он не нужен ни одному блоку, поэтому попасть на экран
//    не может даже данными. Действий отправки текстовых сообщений НЕТ: у ELN они выключены
//    до одобрения A2P (sofia_persona `_eln_config`, флаг отправки = False), и экран вообще
//    ничего не отправляет.
//
// 🔴 ЧЕТЫРЕ СОСТОЯНИЯ У КАЖДОГО БЛОКА: загрузка / готово / отказ 403 / сбой. Пустота и
//    отказ никогда не рисуются нулём. «Календарь не подключён» ≠ «консультаций нет»;
//    «входящих не записано» ≠ «у каждого входящего есть обращение»;
//    формат «неизвестен» ≠ «не записан» [PROPOSED — правило формата].
//
// 🔴 НЕ ИМПОРТИРУЕТ модулей другого арендатора (узор, рельс, счётчики навигации) и их классов:
//    оформление — только классы `tt-*` из TenantToday.css на токенах shadcn.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import taskService from '../services/taskService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import {
    ZAGRUZKA,
    rodOshibki,
    leadIdIzSobytiya,
    formatVstrechi,
    granitsySutok,
    klyuchPrivetstviya,
    lokal,
    vremya,
    data,
    dataSroka,
    adresGlavnogoOfisa,
    usloviyaKonsultacii,
} from './tenantTodayLogic';
import './TenantToday.css';

const OKNO_CHASOV = 24;          // окно блоков «за сутки» — один держатель
const PREDEL_ZAPISEY = 20;       // сколько будущих записей просит блок «Bookings»
const PACHKA_FORMATOV = 50;      // верхняя граница ручки consultation-formats (договор 09-01)

// Загрузка одного блока: своё обещание, свой флаг жизни, отказ ≠ сбой.
const useBlok = (zagruzit, prichinaNeNazvana) => {
    const [blok, postavit] = useState(ZAGRUZKA);
    useEffect(() => {
        let zhiv = true;
        zagruzit()
            .then((r) => { if (zhiv) postavit({ state: 'ready', data: r, reason: null }); })
            .catch((err) => {
                if (zhiv) {
                    postavit({ state: rodOshibki(err), data: null, reason: (err && err.message) || prichinaNeNazvana });
                }
            });
        return () => { zhiv = false; };
        // Загрузчик и подпись причины намеренно берутся на момент монтирования: смена языка
        // не должна перезапрашивать данные.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return blok;
};

const Blok = ({ klass, title, to, linkLabel, caveat, children }) => (
    <section className={`tt-block ${klass}`}>
        <header className="tt-block-head">
            <h2 className="tt-block-title">{title}</h2>
            {to ? <Link className="tt-block-link" to={to}>{linkLabel}</Link> : null}
        </header>
        <div className="tt-block-body">{children}</div>
        {caveat ? <div className="tt-caveat">{caveat}</div> : null}
    </section>
);

const Sostoyanie = ({ blok, render, t }) => {
    if (blok.state === 'loading') return <p className="tt-state">{t('state.loading')}</p>;
    if (blok.state === 'denied') return <p className="tt-state tt-state--denied">{t('state.denied', { reason: blok.reason })}</p>;
    if (blok.state === 'failed') return <p className="tt-state tt-state--failed">{t('state.failed', { reason: blok.reason })}</p>;
    return render(blok.data);
};

const Pusto = ({ children }) => <p className="tt-empty">{children}</p>;

// Разбить список id на пачки по PACHKA_FORMATOV (контракт ручки: 1..50).
const pachki = (ids) => {
    const out = [];
    for (let i = 0; i < ids.length; i += PACHKA_FORMATOV) out.push(ids.slice(i, i + PACHKA_FORMATOV));
    return out;
};

const TenantToday = () => {
    const { user } = useAuth();
    const { branding } = useBranding();
    const { t, lang } = useUiLanguage();
    const bezPrichiny = t('state.reasonUnknown');

    const calendarStatus = useBlok(() => apiService.getTodayCalendarStatus(), bezPrichiny);
    const events = useBlok(() => apiService.getCalendarEvents(...granitsySutok()), bezPrichiny);
    const bookings = useBlok(() => apiService.getTodayBookings(PREDEL_ZAPISEY), bezPrichiny);
    const tasks = useBlok(() => taskService.getTasks({ assigned_to_me: true }), bezPrichiny);
    const deadline = useBlok(() => apiService.getBlizhayshiySrok(), bezPrichiny);
    const inquiries = useBlok(() => apiService.getTodayInquiries(OKNO_CHASOV), bezPrichiny);
    const sofia = useBlok(() => apiService.getTodaySofiaActivity(OKNO_CHASOV), bezPrichiny);
    const lost = useBlok(() => apiService.getTodayCallsWithoutInquiry(OKNO_CHASOV), bezPrichiny);
    const ofisy = useBlok(() => apiService.getOfisy(), bezPrichiny);

    // ── Формат встреч [PROPOSED — правило формата] ─────────────────────────────
    // Источник — НЕ bookings (там только будущие, limit 20: утренняя встреча получила бы
    // ложное «не записан»), а отдельная ручка по Lead ID сегодняшних событий.
    const sobytiya = useMemo(
        () => (events.state === 'ready' && Array.isArray(events.data) ? events.data : []),
        [events],
    );
    const [formaty, postavitFormaty] = useState(ZAGRUZKA);
    useEffect(() => {
        if (events.state !== 'ready') return undefined;
        const ids = Array.from(new Set(sobytiya.map(leadIdIzSobytiya).filter((x) => x !== null)));
        if (ids.length === 0) {
            postavitFormaty({ state: 'ready', data: null, reason: null });
            return undefined;
        }
        let zhiv = true;
        Promise.all(pachki(ids).map((p) => apiService.getTodayConsultationFormats(p)))
            .then((otvety) => {
                if (!zhiv) return;
                const slito = { formats: {}, unknown_ids: [] };
                otvety.forEach((o) => {
                    Object.assign(slito.formats, (o && o.formats) || {});
                    slito.unknown_ids.push(...((o && o.unknown_ids) || []));
                });
                postavitFormaty({ state: 'ready', data: slito, reason: null });
            })
            .catch((err) => {
                if (zhiv) postavitFormaty({ state: rodOshibki(err), data: null, reason: (err && err.message) || null });
            });
        return () => { zhiv = false; };
    }, [events.state, sobytiya]);

    // ── Стенограмма по клику: одна открытая строка за раз ─────────────────────
    const [otkryto, postavitOtkryto] = useState(null);           // {vid: 'call'|'chat', id}
    const [stenogramma, postavitStenogrammu] = useState(ZAGRUZKA);
    const pereklyuchit = useCallback((vid, id) => {
        if (otkryto && otkryto.vid === vid && otkryto.id === id) {
            postavitOtkryto(null);
            return;
        }
        postavitOtkryto({ vid, id });
        postavitStenogrammu(ZAGRUZKA);
        const zapros = vid === 'call'
            ? apiService.getTodayCallTranscript(id)
            : apiService.getTodayChatSession(id);
        zapros
            .then((r) => postavitStenogrammu({ state: 'ready', data: r, reason: null }))
            .catch((err) => postavitStenogrammu({
                state: rodOshibki(err), data: null, reason: (err && err.message) || bezPrichiny,
            }));
    }, [otkryto, bezPrichiny]);

    // ── Производные величины ───────────────────────────────────────────────────
    const adres = ofisy.state === 'ready' ? adresGlavnogoOfisa(ofisy.data) : null;
    const glavnyyOfis = ofisy.state === 'ready' && ofisy.data && Array.isArray(ofisy.data.ofisy)
        ? ofisy.data.ofisy.find((o) => o && o.glavnyy === true) : null;
    const firma = (branding && branding.company_name) || (glavnyyOfis && glavnyyOfis.firma) || null;
    const usloviya = bookings.state === 'ready' ? usloviyaKonsultacii(bookings.data) : null;
    const strokaUsloviy = usloviya ? t(usloviya.kluch, { minutes: usloviya.minutes }) : null;

    const kalendarNePodklyuchen = calendarStatus.state === 'ready'
        && calendarStatus.data && calendarStatus.data.status === 'not_connected';
    const statusKalendaryaNeizvesten = calendarStatus.state === 'denied' || calendarStatus.state === 'failed';

    const podpisFormata = (sobytie) => {
        if (leadIdIzSobytiya(sobytie) !== null && formaty.state === 'loading') return t('state.loading');
        return t(`meets.format.${formatVstrechi(sobytie, formaty.state === 'ready' ? formaty.data : null)}`);
    };

    const imyaPolzovatelya = user && user.first_name ? `, ${user.first_name}` : '';

    // ── Разметка ──────────────────────────────────────────────────────────────
    const stenogrammaUzel = () => (
        <div className="tt-transcript">
            <Sostoyanie t={t} blok={stenogramma} render={(d) => {
                const repliki = (d && Array.isArray(d.messages)) ? d.messages : [];
                if (repliki.length === 0 && !(d && d.transcript_text)) return <Pusto>{t('sofia.noTranscript')}</Pusto>;
                return (
                    <>
                        {repliki.map((m, i) => (
                            <p key={i} className="tt-replika">
                                <span className="tt-role">
                                    {m.role === 'assistant' ? t('sofia.role.assistant') : t('sofia.role.user')}
                                </span>{' '}
                                <span>{m.content}</span>
                            </p>
                        ))}
                        {repliki.length === 0 && d.transcript_text
                            ? <pre className="tt-transcript-text">{d.transcript_text}</pre> : null}
                    </>
                );
            }} />
        </div>
    );

    const knopkaStenogrammy = (vid, id) => {
        const otkrytaEta = otkryto && otkryto.vid === vid && otkryto.id === id;
        return (
            <button type="button" className="tt-btn" onClick={() => pereklyuchit(vid, id)}>
                {otkrytaEta ? t('sofia.hide') : t('sofia.show')}
            </button>
        );
    };

    return (
        <div className="tt-page">
            <header className="tt-head">
                <p className="tt-overline">{t('today.overline')}</p>
                <h1 className="tt-title">{`${t(klyuchPrivetstviya(new Date().getHours()))}${imyaPolzovatelya}`}</h1>
                <p className="tt-sub">
                    {new Date().toLocaleDateString(lokal(lang), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    {firma ? <span className="tt-firm"> · {firma}</span> : null}
                </p>
            </header>

            <div className="tt-grid">
                {/* 1. Консультации сегодня */}
                <Blok klass="tt-meets" title={t('meets.title')} to="/calendar" linkLabel={t('meets.link')}
                    caveat={<>{t('meets.caveat')}{strokaUsloviy ? <span className="tt-terms"> {strokaUsloviy}</span> : null}</>}>
                    {kalendarNePodklyuchen ? (
                        <p className="tt-state tt-state--notconnected">{t('meets.notConnected')}</p>
                    ) : (
                        <>
                            {statusKalendaryaNeizvesten
                                ? <p className="tt-state">{t('meets.statusUnknown', { reason: calendarStatus.reason })}</p> : null}
                            <Sostoyanie t={t} blok={events} render={() => (sobytiya.length === 0
                                ? <Pusto>{t('meets.none')}</Pusto>
                                : (
                                    <ul className="tt-list">
                                        {sobytiya.map((e, i) => {
                                            const f = formaty.state === 'ready' ? formatVstrechi(e, formaty.data) : null;
                                            return (
                                                <li key={e.id || i} className="tt-row">
                                                    <span className="tt-time">{vremya(e.start && (e.start.dateTime || e.start.date), lang)}</span>
                                                    <span className="tt-main">{e.summary || '—'}</span>
                                                    {e.attorney_name ? <span className="tt-meta">{e.attorney_name}</span> : null}
                                                    <span className="tt-meta">{podpisFormata(e)}</span>
                                                    {f === 'in_person' && adres
                                                        ? <span className="tt-meta">{t('meets.at', { address: adres })}</span> : null}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ))} />
                        </>
                    )}
                </Blok>

                {/* 2. Мои задачи */}
                <Blok klass="tt-tasks" title={t('tasks.title')} to="/tasks" linkLabel={t('tasks.link')}>
                    <Sostoyanie t={t} blok={tasks} render={(d) => {
                        const otkrytye = (Array.isArray(d) ? d : []).filter((z) => z && z.status !== 'DONE');
                        if (otkrytye.length === 0) return <Pusto>{t('tasks.none')}</Pusto>;
                        return (
                            <ul className="tt-list">
                                {otkrytye.map((z) => (
                                    <li key={z.id} className="tt-row">
                                        <span className="tt-main">{z.title}</span>
                                        {z.due_date ? <span className="tt-meta">{t('tasks.due', { date: dataSroka(String(z.due_date).slice(0, 10), lang) || data(z.due_date, lang) || '—' })}</span> : null}
                                        {z.is_ai_generated === true ? <span className="tt-pill">{t('tasks.ai')}</span> : null}
                                    </li>
                                ))}
                            </ul>
                        );
                    }} />
                </Blok>

                {/* 3. Ближайший срок */}
                <Blok klass="tt-deadline" title={t('deadline.title')} to="/cases" linkLabel={t('deadline.link')}>
                    <Sostoyanie t={t} blok={deadline} render={(d) => {
                        const dd = d || {};
                        const bezSroka = dd.bez_sroka || {};
                        return (
                            <>
                                {dd.srok ? (
                                    <div className="tt-deadline-main">
                                        <p className="tt-big">
                                            {t('deadline.daysLeft', { days: dd.srok.dney_ostalos })}{' '}
                                            <span className="tt-meta">{t('deadline.until', { date: dataSroka(dd.srok.deadline, lang) || '—' })}</span>
                                        </p>
                                        <p className="tt-row">
                                            <span className="tt-main">{dd.srok.case_number} — {dd.srok.title}</span>{' '}
                                            <Link className="tt-block-link" to={`/cases/${dd.srok.case_id}`}>{t('deadline.open')}</Link>
                                        </p>
                                    </div>
                                ) : <Pusto>{t('deadline.none', { visible: dd.vsego_del_vidno })}</Pusto>}
                                <p className="tt-meta">{t('deadline.counts', {
                                    without: bezSroka.vsego,
                                    visible: dd.vsego_del_vidno,
                                    unknown: dd.priznano_neizvestnymi,
                                    past: dd.proshedshie,
                                })}</p>
                            </>
                        );
                    }} />
                </Blok>

                {/* 4. Новые обращения за сутки */}
                <Blok klass="tt-inquiries" title={t('inq.title')} to="/leads" linkLabel={t('inq.link')}>
                    <Sostoyanie t={t} blok={inquiries} render={(d) => {
                        const dd = d || {};
                        const kanaly = dd.channels || {};
                        const chasov = dd.window_hours || OKNO_CHASOV;
                        const spisok = Array.isArray(dd.items) ? dd.items : [];
                        const drugie = Array.isArray(dd.other_sources) ? dd.other_sources : [];
                        return (
                            <>
                                <p className="tt-big">{t('inq.total', { hours: chasov, total: dd.total })}</p>
                                <ul className="tt-channels">
                                    {['site_form', 'sofia_chat', 'sofia_call'].map((k) => (
                                        <li key={k}>{t(`inq.channel.${k}`)}: {kanaly[k] === undefined ? '—' : kanaly[k]}</li>
                                    ))}
                                </ul>
                                {drugie.length > 0 ? (
                                    <div className="tt-meta">
                                        {t('inq.otherSources')}
                                        <ul className="tt-channels">
                                            {drugie.map((o) => <li key={o.source}>{o.source}: {o.count}</li>)}
                                        </ul>
                                    </div>
                                ) : null}
                                {/* [PROPOSED — правило слитых]: строка есть, только если поле пришло. */}
                                {'merged_excluded' in dd
                                    ? <p className="tt-meta">{t('inq.merged', { n: dd.merged_excluded })}</p> : null}
                                {dd.total === 0 ? <Pusto>{t('inq.none', { hours: chasov })}</Pusto> : null}
                                {spisok.length > 0 ? (
                                    <ul className="tt-list">
                                        {spisok.map((it) => (
                                            <li key={it.id} className="tt-row">
                                                <span className="tt-time">{vremya(it.created_at, lang)}</span>
                                                <Link className="tt-main" to={`/leads/${it.id}`}>{it.full_name || '—'}</Link>
                                                <span className="tt-meta">{t(`inq.channel.${it.channel}`)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                {dd.items_truncated
                                    ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.total })}</p> : null}
                            </>
                        );
                    }} />
                </Blok>

                {/* 5. Записи на консультацию */}
                <Blok klass="tt-bookings" title={t('book.title')}>
                    <Sostoyanie t={t} blok={bookings} render={(d) => {
                        const dd = d || {};
                        const spisok = Array.isArray(dd.items) ? dd.items : [];
                        return (
                            <>
                                {strokaUsloviy ? <p className="tt-terms">{strokaUsloviy}</p> : null}
                                {spisok.length === 0 ? <Pusto>{t('book.none')}</Pusto> : (
                                    <ul className="tt-list">
                                        {spisok.map((it) => (
                                            <li key={`${it.lead_id}-${it.appointment_datetime}`} className="tt-row">
                                                <span className="tt-time">
                                                    {data(it.appointment_datetime, lang) || '—'} {vremya(it.appointment_datetime, lang)}
                                                </span>
                                                <Link className="tt-main" to={`/leads/${it.lead_id}`}>{it.full_name || '—'}</Link>
                                                {/* [PROPOSED — правило формата]: лид здесь найден, поэтому null — «не записан». */}
                                                <span className="tt-meta">{t(`meets.format.${it.format || 'not_recorded'}`)}</span>
                                                <span className="tt-meta">{it.in_calendar ? t('book.inCalendar') : t('book.notInCalendar')}</span>
                                                {it.booking_failed ? <span className="tt-pill tt-pill--warn">{t('book.failed')}</span> : null}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {dd.truncated
                                    ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.total_upcoming })}</p> : null}
                            </>
                        );
                    }} />
                </Blok>

                {/* 6. Звонки и чаты Sofia */}
                <Blok klass="tt-sofia" title={t('sofia.title')}>
                    <Sostoyanie t={t} blok={sofia} render={(d) => {
                        const dd = d || {};
                        const chasov = dd.window_hours || OKNO_CHASOV;
                        const zvonki = (dd.calls && Array.isArray(dd.calls.items)) ? dd.calls.items : [];
                        const chaty = (dd.chats && Array.isArray(dd.chats.items)) ? dd.chats.items : [];
                        return (
                            <>
                                <h3 className="tt-subtitle">{t('sofia.calls', { n: dd.calls && dd.calls.total })}</h3>
                                {zvonki.length === 0 ? <Pusto>{t('sofia.noCalls', { hours: chasov })}</Pusto> : (
                                    <ul className="tt-list">
                                        {zvonki.map((z) => (
                                            <li key={z.id} className="tt-row tt-row--wrap">
                                                <span className="tt-time">{vremya(z.created_at, lang)}</span>
                                                <span className="tt-main">{z.caller_number || '—'}</span>
                                                <span className="tt-meta">{t('sofia.seconds', { n: z.duration_seconds })}</span>
                                                {z.lead_id ? <Link className="tt-meta" to={`/leads/${z.lead_id}`}>{t('sofia.lead', { id: z.lead_id })}</Link> : null}
                                                {z.has_transcript ? knopkaStenogrammy('call', z.id) : null}
                                                {otkryto && otkryto.vid === 'call' && otkryto.id === z.id ? stenogrammaUzel() : null}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {dd.calls && dd.calls.truncated
                                    ? <p className="tt-meta">{t('state.truncated', { shown: zvonki.length, total: dd.calls.total })}</p> : null}

                                <h3 className="tt-subtitle">{t('sofia.chats', { n: dd.chats && dd.chats.total })}</h3>
                                {chaty.length === 0 ? <Pusto>{t('sofia.noChats', { hours: chasov })}</Pusto> : (
                                    <ul className="tt-list">
                                        {chaty.map((c) => (
                                            <li key={c.id} className="tt-row tt-row--wrap">
                                                <span className="tt-time">{vremya(c.created_at, lang)}</span>
                                                <span className="tt-main">{t('sofia.turns', { n: c.turn_count })}</span>
                                                {c.language ? <span className="tt-meta">{c.language}</span> : null}
                                                {c.lead_id ? <Link className="tt-meta" to={`/leads/${c.lead_id}`}>{t('sofia.lead', { id: c.lead_id })}</Link> : null}
                                                {c.has_transcript ? knopkaStenogrammy('chat', c.id) : null}
                                                {otkryto && otkryto.vid === 'chat' && otkryto.id === c.id ? stenogrammaUzel() : null}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {dd.chats && dd.chats.truncated
                                    ? <p className="tt-meta">{t('state.truncated', { shown: chaty.length, total: dd.chats.total })}</p> : null}
                            </>
                        );
                    }} />
                </Blok>

                {/* 7. Звонки без обращения */}
                <Blok klass="tt-lost" title={t('lost.title')}
                    caveat={lost.state === 'ready' && lost.data ? t('lost.caveat', {
                        hours: lost.data.window_hours,
                        leadHours: lost.data.lead_window_hours,
                        matched: lost.data.matched_by_phone,
                    }) : null}>
                    <Sostoyanie t={t} blok={lost} render={(d) => {
                        const dd = d || {};
                        const chasov = dd.window_hours || OKNO_CHASOV;
                        const spisok = Array.isArray(dd.items) ? dd.items : [];
                        return (
                            <>
                                <p className="tt-big">{t('lost.denominator', { n: spisok.length, total: dd.inbound_total })}</p>
                                {dd.inbound_total === 0 ? <Pusto>{t('lost.noInbound', { hours: chasov })}</Pusto> : null}
                                {dd.inbound_total > 0 && spisok.length === 0 ? <Pusto>{t('lost.none', { hours: chasov })}</Pusto> : null}
                                {spisok.length > 0 ? (
                                    <ul className="tt-list">
                                        {spisok.map((z) => (
                                            <li key={z.id} className="tt-row tt-row--wrap">
                                                <span className="tt-time">{vremya(z.created_at, lang)}</span>
                                                <span className="tt-main">{z.anonymous ? t('lost.anonymous') : (z.caller_number || '—')}</span>
                                                <span className="tt-meta">{t('sofia.seconds', { n: z.duration_seconds })}</span>
                                                {z.duration_seconds === 0 && !z.has_transcript
                                                    ? <span className="tt-pill tt-pill--warn">{t('lost.zeroNoTranscript')}</span> : null}
                                                {z.reason ? <span className="tt-meta">{t('lost.reason', { reason: z.reason })}</span> : null}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                {dd.truncated
                                    ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.inbound_total })}</p> : null}
                            </>
                        );
                    }} />
                </Blok>
            </div>
        </div>
    );
};

export default TenantToday;
